import { useState, useEffect } from "react";
import { Check, X, Clock, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const statusFilters = ["all", "pending", "approved", "rejected"];

export default function AdminRequests() {
  const { api } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selectedReq, setSelectedReq] = useState(null);

  const fetchRequests = () => {
    setLoading(true);
    const params = filter !== "all" ? `?status=${filter}` : "";
    api.get(`/requests${params}`)
      .then((r) => setRequests(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchRequests(); }, [filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAction = async (requestId, status) => {
    try {
      await api.put(`/requests/${requestId}`, { status });
      toast.success(`Request ${status}`);
      fetchRequests();
      setSelectedReq(null);
    } catch (e) {
      toast.error("Failed to update request");
    }
  };

  const statusBadge = (status) => {
    switch (status) {
      case "pending": return <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 text-xs gap-1"><Clock className="w-3 h-3" />Pending</Badge>;
      case "approved": return <Badge variant="outline" className="text-emerald-600 border-emerald-200 bg-emerald-50 text-xs gap-1"><Check className="w-3 h-3" />Approved</Badge>;
      case "rejected": return <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50 text-xs gap-1"><X className="w-3 h-3" />Rejected</Badge>;
      default: return null;
    }
  };

  return (
    <div data-testid="admin-requests-page">
      <div className="mb-8">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-slate-400 mb-2">Management</p>
        <h1 className="font-serif text-3xl font-bold text-slate-900 tracking-tight">Requests</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 mb-6">
        {statusFilters.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-sans capitalize transition-colors ${
              filter === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`}
            data-testid={`filter-request-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="spinner"></div></div>
      ) : requests.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <p className="font-sans text-sm text-slate-400">No requests found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {requests.map((req) => (
            <div
              key={req.request_id}
              className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4 cursor-pointer hover:shadow-sm transition-all"
              onClick={() => setSelectedReq(req)}
              data-testid={`request-row-${req.request_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-1">
                  <span className="font-sans text-sm font-medium text-slate-900">{req.user_name}</span>
                  <span className="font-mono text-xs text-slate-400">{req.user_email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs capitalize">{req.type}</Badge>
                  <span className="font-sans text-sm text-slate-500">for {req.project_name}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="font-mono text-xs text-slate-400">{new Date(req.created_at).toLocaleDateString()}</span>
                {statusBadge(req.status)}
              </div>
              {req.status === "pending" && (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleAction(req.request_id, "approved")}
                    className="text-emerald-500 hover:text-emerald-600 hover:bg-emerald-50"
                    data-testid={`approve-request-${req.request_id}`}
                  >
                    <Check className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => handleAction(req.request_id, "rejected")}
                    className="text-red-400 hover:text-red-500 hover:bg-red-50"
                    data-testid={`reject-request-${req.request_id}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Request Detail Dialog */}
      <Dialog open={!!selectedReq} onOpenChange={() => setSelectedReq(null)}>
        <DialogContent className="max-w-lg" data-testid="request-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl capitalize">{selectedReq?.type} Request</DialogTitle>
          </DialogHeader>
          {selectedReq && (
            <div className="space-y-4 mt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-sans text-base font-medium text-slate-900">{selectedReq.user_name}</p>
                  <p className="font-mono text-sm text-slate-500">{selectedReq.user_email}</p>
                </div>
                {statusBadge(selectedReq.status)}
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-mono text-xs tracking-wider uppercase text-slate-400 mb-2">Project</p>
                <p className="font-sans text-sm text-slate-900 font-medium">{selectedReq.project_name}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="font-mono text-xs tracking-wider uppercase text-slate-400 mb-2">Form Data</p>
                {Object.entries(selectedReq.form_data || {}).map(([key, value]) => (
                  <div key={key} className="mb-3">
                    <p className="font-sans text-xs text-slate-500 capitalize mb-0.5">{key.replace(/_/g, " ")}</p>
                    <p className="font-sans text-sm text-slate-700">
                      {Array.isArray(value) ? value.join(", ") : value || "—"}
                    </p>
                  </div>
                ))}
              </div>
              <p className="font-mono text-xs text-slate-400">
                Submitted: {new Date(selectedReq.created_at).toLocaleString()}
              </p>
              {selectedReq.status === "pending" && (
                <div className="flex gap-3">
                  <Button
                    onClick={() => handleAction(selectedReq.request_id, "approved")}
                    className="flex-1 bg-emerald-600 text-white hover:bg-emerald-700 font-sans gap-2"
                    data-testid="dialog-approve-btn"
                  >
                    <Check className="w-4 h-4" /> Approve
                  </Button>
                  <Button
                    onClick={() => handleAction(selectedReq.request_id, "rejected")}
                    variant="outline"
                    className="flex-1 border-red-200 text-red-600 hover:bg-red-50 font-sans gap-2"
                    data-testid="dialog-reject-btn"
                  >
                    <X className="w-4 h-4" /> Reject
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
